import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, History, StickyNote, User, X, Trash2, Zap, Sparkles } from 'lucide-react';
import { useVisitor } from '@/hooks/useVisitor';
import { useAnnouncements } from '@/hooks/useAnnouncements';

/**
 * Floating visitor identity + panel with Bookmarks / Visited / Notes / Offers tabs.
 * Persists to localStorage (no login needed).
 */
export default function VisitorPanel({ booths = [] }) {
  const { name, setName, bookmarks, visited, notes, toggleBookmark, setNote, clearAll } = useVisitor();
  const { announcements, unseenCount, markAllSeen } = useAnnouncements();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('bookmarks');
  const [nameDraft, setNameDraft] = useState(name);

  const boothById = React.useMemo(() => Object.fromEntries(booths.map((b) => [b.id, b])), [booths]);

  const bookmarkList = bookmarks.map((id) => boothById[id]).filter(Boolean);
  const visitedEntries = Object.entries(visited)
    .map(([id, ts]) => ({ booth: boothById[Number(id)], ts }))
    .filter((v) => v.booth)
    .sort((a, b) => new Date(b.ts) - new Date(a.ts));
  const noteEntries = Object.entries(notes)
    .map(([id, text]) => ({ booth: boothById[Number(id)], text }))
    .filter((v) => v.booth);

  const totalBadge = unseenCount;   // red pulse count = only new/unseen offers

  return (
    <>
      {/* Floating trigger button (bottom-right) */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white rounded-full p-4 shadow-2xl shadow-emerald-500/30 border-2 border-emerald-300/40 transition-all hover:scale-105"
        data-testid="visitor-panel-btn"
        title={name ? `Hi, ${name}` : 'Guest Mode'}
      >
        <User className="w-5 h-5" />
        {/* Bookmark count (yellow) */}
        {bookmarks.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-yellow-400 text-slate-900 text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center border-2 border-slate-900" data-testid="visitor-bookmark-badge">
            {bookmarks.length}
          </span>
        )}
        {/* Unseen offers pulse (red) */}
        {totalBadge > 0 && (
          <span
            className="absolute -bottom-1 -right-1 bg-red-500 text-white text-[10px] font-black rounded-full min-w-[22px] h-[22px] px-1 flex items-center justify-center border-2 border-slate-900 animate-pulse shadow-lg shadow-red-500/50"
            data-testid="visitor-offers-badge"
          >
            <Zap className="w-2.5 h-2.5 mr-0.5" />{totalBadge}
          </span>
        )}
      </button>

      {/* Slide-in drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <aside
            className="relative w-full max-w-md h-full bg-slate-950 border-l border-emerald-500/30 shadow-2xl flex flex-col animate-in slide-in-from-right-1/2 duration-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="visitor-panel"
          >
            {/* Header */}
            <div className="p-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/60">
              <div>
                <div className="text-emerald-400 text-[10px] uppercase tracking-widest font-bold">Guest Mode</div>
                <div className="text-white font-bold text-lg">
                  {name ? `Hi, ${name}!` : 'Welcome, Visitor'}
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white p-1" data-testid="close-visitor-panel">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Name capture */}
            <div className="p-4 border-b border-slate-800">
              <label className="text-xs text-slate-400 uppercase font-bold tracking-widest mb-1 block">Your Name (optional)</label>
              <div className="flex gap-2">
                <input
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  placeholder="e.g. Ahmad"
                  maxLength={30}
                  className="flex-1 bg-slate-900 border border-slate-700 focus:border-emerald-500 rounded-lg px-3 py-2 text-white text-sm outline-none"
                  data-testid="visitor-name-input"
                />
                <button
                  onClick={() => setName(nameDraft.trim())}
                  className="px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold"
                  data-testid="visitor-save-name"
                >
                  Save
                </button>
              </div>
            </div>

            {/* Sub-tabs */}
            <div className="flex border-b border-slate-800">
              {[
                { key: 'bookmarks', label: `⭐ ${bookmarkList.length}`, Icon: Star },
                { key: 'visited', label: `${visitedEntries.length}`, Icon: History },
                { key: 'notes', label: `${noteEntries.length}`, Icon: StickyNote },
                { key: 'offers', label: `${announcements.length}`, Icon: Zap, hasBadge: unseenCount > 0 },
              ].map(({ key, label, Icon, hasBadge }) => (
                <button
                  key={key}
                  onClick={() => {
                    setTab(key);
                    if (key === 'offers') markAllSeen();
                  }}
                  className={`relative flex-1 py-3 text-xs font-bold flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
                    tab === key ? 'text-emerald-400 border-emerald-500' : 'text-slate-400 border-transparent hover:text-white'
                  }`}
                  data-testid={`visitor-tab-${key}`}
                >
                  <Icon className="w-4 h-4" /> {label}
                  {hasBadge && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 animate-pulse" data-testid="visitor-offers-tab-dot" />
                  )}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {tab === 'bookmarks' && bookmarkList.length === 0 && (
                <EmptyState icon={Star} msg="No bookmarks yet. Tap the star on any booth to save it here." />
              )}
              {tab === 'bookmarks' && bookmarkList.map((b) => (
                <BoothCard key={b.id} booth={b} onRemove={() => toggleBookmark(b.id)} removeLabel="Remove" testid={`bookmark-${b.id}`} />
              ))}

              {tab === 'visited' && visitedEntries.length === 0 && (
                <EmptyState icon={History} msg="You haven't visited any booth pages yet." />
              )}
              {tab === 'visited' && visitedEntries.map(({ booth, ts }) => (
                <BoothCard key={booth.id} booth={booth} subtitle={new Date(ts).toLocaleString()} testid={`visited-${booth.id}`} />
              ))}

              {tab === 'notes' && noteEntries.length === 0 && (
                <EmptyState icon={StickyNote} msg="Open any booth and tap 'Add Note' to jot down your thoughts." />
              )}
              {tab === 'notes' && noteEntries.map(({ booth, text }) => (
                <div key={booth.id} className="bg-slate-900/60 rounded-lg p-3 border border-slate-800" data-testid={`note-${booth.id}`}>
                  <Link to={`/booth/${booth.id}`} className="text-emerald-400 font-bold text-sm hover:underline">
                    {booth.booth_number} — {booth.company?.name || 'Booth'}
                  </Link>
                  <textarea
                    value={text}
                    onChange={(e) => setNote(booth.id, e.target.value)}
                    className="w-full mt-2 bg-slate-950 border border-slate-800 rounded p-2 text-slate-200 text-xs resize-none"
                    rows={3}
                  />
                </div>
              ))}

              {tab === 'offers' && announcements.length === 0 && (
                <EmptyState icon={Sparkles} msg="No live offers right now. Check back later!" />
              )}
              {tab === 'offers' && announcements.map((a) => {
                const isFlash = a.type === 'flash_sale';
                return (
                  <Link
                    key={a.id}
                    to={`/redeem/${a.coupon_code}`}
                    onClick={() => setOpen(false)}
                    className={`block rounded-lg p-3 border transition-colors ${
                      isFlash ? 'border-red-500/40 bg-gradient-to-br from-red-900/30 to-orange-900/20 hover:border-red-400/70' : 'border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-400/60'
                    }`}
                    data-testid={`offer-${a.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <Zap className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isFlash ? 'text-red-400' : 'text-emerald-400'}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-[9px] uppercase tracking-widest font-black ${isFlash ? 'text-red-400' : 'text-emerald-400'}`}>
                          {isFlash ? 'FLASH DEAL' : a.type.toUpperCase()} · {a.company?.name || 'Booth'}
                        </div>
                        <div className="text-white text-sm font-bold mt-0.5 truncate">{a.title}</div>
                        {a.discount_label && (
                          <div className="mt-1 inline-block bg-yellow-400 text-slate-900 text-[10px] font-black px-1.5 py-0.5 rounded">
                            {a.discount_label}
                          </div>
                        )}
                        <div className="mt-1 text-slate-400 text-[10px] font-mono">Code: {a.coupon_code}</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Footer */}
            {(bookmarkList.length > 0 || visitedEntries.length > 0 || noteEntries.length > 0) && (
              <div className="border-t border-slate-800 p-3">
                <button
                  onClick={() => { if (window.confirm('Clear all bookmarks, notes and history?')) clearAll(); }}
                  className="w-full text-xs text-red-400 hover:text-red-300 flex items-center justify-center gap-1 py-2"
                  data-testid="visitor-clear-all"
                >
                  <Trash2 className="w-3 h-3" /> Clear all visitor data
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}

function EmptyState({ icon: Icon, msg }) {
  return (
    <div className="py-12 text-center text-slate-500">
      <Icon className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <div className="text-xs">{msg}</div>
    </div>
  );
}

function BoothCard({ booth, subtitle, onRemove, removeLabel, testid }) {
  return (
    <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-800 hover:border-emerald-500/40 transition-colors" data-testid={testid}>
      <Link to={`/booth/${booth.id}`} className="block">
        <div className="text-white font-bold text-sm">{booth.company?.name || booth.booth_number}</div>
        <div className="text-slate-400 text-xs mt-0.5">{booth.booth_number} · {booth.structure_type || 'booth'}</div>
        {subtitle && <div className="text-slate-500 text-[10px] mt-1">{subtitle}</div>}
      </Link>
      {onRemove && (
        <button onClick={onRemove} className="mt-2 text-[10px] text-red-400 hover:text-red-300 font-bold uppercase tracking-widest">
          {removeLabel}
        </button>
      )}
    </div>
  );
}
